<script setup>
// Libraries
import { ref, computed, reactive, onMounted, watch } from 'vue'
import { EventBus } from '@common/lib/event-bus'

// Components
import CompetitorTile from '@src/modules/analytics_v3/components/CompetitorTile.vue'
import PerformanceComparison from '@src/modules/analytics_v3/components/PerformanceComparison.vue'
import CompetitorsTable from '@src/modules/analytics_v3/components/CompetitorsTable.vue'
import FollowersComparisonBarChart from '@src/modules/analytics_v3/components/FollowersComparisonBarChart.vue'
import SkeletonBox from '@src/modules/analytics/views/common/SkeletonBox.vue'
import ReportFooter from '@src/modules/analytics/components/reports/ReportFooter.vue'
import DoubleBarChart from '@src/modules/analytics_v3/components/DoubleBarChart.vue'
import TopPerformingPostsReport from '@src/modules/analytics_v3/components/TopPerformingPostsReport.vue'
import LeastPerformingPostsReport from '@src/modules/analytics_v3/components/LeastPerformingPostsReport.vue'

// Constants
import { PLATFORMS } from '@src/modules/analytics_v3/constants/constants.js'

// Composables
import useCompetitorReport from '@src/modules/analytics_v3/composables/useCompetitorReport.js'
import useDataTableMetrics from '@src/modules/analytics_v3/composables/useDataTableMetrics.js'
import usePostingActivityGraphByTypes from '@src/modules/analytics_v3/composables/usePostingActivityGraphByTypes.js'
import usePostingBySpecificType from '@src/modules/analytics_v3/composables/usePostingBySpecificType.js'
import useFollowersGrowthComparison from '@src/modules/analytics_v3/composables/useFollowersGrowthComparison.js'
import useTopHashtags from '@src/modules/analytics_v3/composables/useTopHashtags.js'
import useIndividualHashtagData from '@src/modules/analytics_v3/composables/useIndividualHashtagData.js'
import useBiographyData from '@src/modules/analytics_v3/composables/useBiographyData.js'
import useTopAndLeastPerformingPosts from '@src/modules/analytics_v3/composables/useTopAndLeastPerformingPosts.js'
import useCompetitorsFactory from '@src/modules/analytics_v3/composables/useCompetitorsFactory.js'

// Props
const props = defineProps({
  platform: {
    type: String,
    default: PLATFORMS.INSTAGRAM,
  },
  competitorReportId: {
    type: [String, Boolean],
    default: false
  },
  reportDate: {
    type: String,
    default: ''
  }
})

// Composable Destructuring
const { platform: platformName } = useCompetitorsFactory()

const {
  getDataTableMetrics,
  dataTableMetricsResponse,
} = useDataTableMetrics()

const {
  getPostingActivityGraphByTypes,
  postingActivityGraphByTypesResponse,
  dateRangeDiff,
  mediaType,
} = usePostingActivityGraphByTypes()

const {
  getPostingActivityBySpecificType,
  postingActivitySpecificTypeResponse,
} = usePostingBySpecificType()

const {
  getFollowersGrowthComparison,
  followersGrowthComparisonResponse,
} = useFollowersGrowthComparison()

const { getTopHashtags, topHashtagsResponse } =
  useTopHashtags()

const { getIndividualHashtag, individualHashtagDataResponse } =
  useIndividualHashtagData()

const { getBiographyData, biographyDataResponse } =
  useBiographyData()

const {
  getTopAndLeastPerformingPosts,
  topAndLeastPerformingPostsResponse,
} = useTopAndLeastPerformingPosts()

const { getReportInfo, reportId, setDateValues } = useCompetitorReport(props.competitorReportId)

// Create chunks of paired items from the remaining data after the first item
const paginatedPostTypeChunks = computed(() => {
  const remainingItems = [...specificPostingTypeResponses.value.slice(1)]
  const chunks = []

  for (let i = 0; i < remainingItems.length; i += 2) {
    chunks.push(remainingItems.slice(i, i + 2))
  }

  return chunks
})
// State and Constants
const state = reactive({
  labelBgColor: '#ffff',
  boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
  maxER: 0,
  minER: 0,
  maxFollowers: 0,
  minFollowers: 0,
  loading: true,
})

const colors = [
  '#5470c6',
  '#91cc75',
  '#fac858',
  '#ee6666',
  '#73c0de',
  '#3ba272',
  '#fc8452',
  '#9a60b4',
  '#ea7ccc',
]

const specificPostingContent = ref(null)
const totalPages = ref(6)

// Computed Properties
const filteredDataTableMetrics = computed(() => {
  return dataTableMetricsResponse.value?.filter(
      (item) => item.state !== 'Added'
  )
})

const reportCompetitorsInfo = computed(() => ({
  reportId: 0,
  platform: PLATFORMS.INSTAGRAM,
}))

const getFollowersGrowthData = computed(() => ({
  data: followersGrowthComparisonResponse.value,
  xAxisLabel: 'Timeline',
  yAxisLabel: 'Followers',
  title: 'Followers Growth Comparison'
}))

const getTopAndLeastPostsData = computed(() => topAndLeastPerformingPostsResponse.value || [])

const getAllAvailablePostsTypes = computed(() =>
    postingActivityGraphByTypesResponse.value?.map((item) => getFormattedMediaType(item.mediaType))
)

const performanceComparisonData = computed(() => ({
  title: "Competitors' Performance Comparison",
  xAxisLabel: 'Engagement Rate (%)',
  yAxisLabel: 'Followers',
}))

const performanceReviewTableData = computed(() => ({
  title: 'Performance Overview',
  headers: [
    'followersCount',
    // 'followersCountDiff',
    'averagePostsPerWeek',
    // 'averagePostsPerWeekDiff',
    'engagementRate',
    // 'engagementRateDiff',
  ],
  type: 'performanceReviewTable',
}))

const performanceReviewGraphData = computed(() => ({
  title: 'Followers Comparison',
  xAxisLabel: 'Competitors',
  yAxisLabel: 'Followers',
}))

const postingByTypeData = {
  xAxisLabel: 'Post Type',
  yAxisLabel: 'Average Engagement',
  yAxisSecondLabel: 'Posts/ Week',
  xAxisSecondLabel: 'Competitors',
  title: 'Activity by Competitors',
  colors,
}

// data for specific activity graph, maps the colors w.r.t specific competitors & then filter on the basis of state.
const postingBySpecificType = computed(() => {
  const data = postingActivitySpecificTypeResponse.value
      ?.map((item) => {
        for (const competitor of dataTableMetricsResponse.value) {
          if (competitor.name === item.name) {
            if (competitor.state === 'Added') {
              return null
            }
            return {...item, color: competitor.color}
          }
        }
        return null
      })
      .filter((competitor) => competitor && competitor.color)

  return {
    xAxisLabel: 'Competitors',
    yAxisLabel: 'Average Engagement',
    yAxisSecondLabel: 'Posts/ Week',
    title: 'Activity by Competitors',
    dateRangeDiff: dateRangeDiff.value,
    data,
  }
})
// data for most engaged hashtags (table)
const hashtagsTableData = {
  headers: [
    'tag',
    'companies_using',
    'count',
    'engagement_per_post',
    'engagement_per_follower',
    'engagement_rate_by_follower',
  ],
  type: 'mostEngagedHashtagsTable',
  title: 'Most Engaged Hashtags',
}

// data for single hashtag data w.r.t competitors
const singleHashtagContent = {
  headers: [
    'name',
    'count',
    'engagement_per_post',
    'engagement_per_follower',
    'engagement_rate_by_follower',
  ],
  type: 'singleHashtagTableData',
}

const getPerformanceComparisonLegends = computed(() =>
  filteredDataTableMetrics.value?.map((item) =>
    item.name ? item.name : item.slug
  )
)

// Bio analysis table data configuration
const bioAnalysisTableData = {
  title: 'Biography Analysis',
  headers: ['biography', 'biography_length'],
  type: 'bioAnalysisTableData',
}

// This computed property returns filtered Instagram analytics data for CompetitorsTable having type bioAnalysisTableData.
const getBioAnalysisTableData = computed(() =>
  biographyDataResponse.value?.filter((competitor) => competitor.state !== 'Added')
)

const getTileGraphValue = (index) => {
  if (!dataTableMetricsResponse.value?.[index]) {
    return 'default'
  }
  const engagementRate = dataTableMetricsResponse.value[index].engagementRate
  return engagementRate > 0 ? 'up' : (engagementRate < 0 ? 'down' : 'default')
}

const fetchSingleHashtagData = (hashtag = '') => {
  getIndividualHashtag(PLATFORMS.INSTAGRAM, hashtag)
}

const specificPostingTypeResponses = ref([])


const fetchSpecificPostTypeData = async (reportId) => {
  const typeMap = {
    'Reel': { type: 'VIDEO REEL', mediaProduct: 'REELS' },
    'Carousel': { type: 'CAROUSEL ALBUM', mediaProduct: 'FEED' },
    'Image': { type: 'IMAGE', mediaProduct: 'FEED' },
    'Video': { type: 'VIDEO', mediaProduct: 'FEED' },
  }

  specificPostingTypeResponses.value = []

  // Make sure we have the available types from the computed property
  const availableTypes = getAllAvailablePostsTypes.value
  console.log('Available post types:', availableTypes)

  // Process only the post types that are available
  const validEntries = []
  Object.entries(typeMap).forEach(([label, config]) => {
    if (availableTypes.some(type => type === label)) {
      validEntries.push([label, config])
    }
  })

  console.log('Valid post type entries:', validEntries)

  for (const [label, { type, mediaProduct }] of validEntries) {
    await getPostingActivityBySpecificType(PLATFORMS.INSTAGRAM, type, mediaProduct, reportId)
    specificPostingTypeResponses.value.push({
      label,
      type,
      mediaProduct,
      activityData: postingActivitySpecificTypeResponse.value,
    })
  }
}


const getFormattedMediaType = (value) => {
  const mediaMapping = {
    'VIDEO REEL': 'Reel',
    'CAROUSEL ALBUM': 'Carousel',
    'IMAGE': 'Image',
    'VIDEO': 'Video'
  }
  return mediaMapping[value ?? mediaType.value] || ''
}

watch(getAllAvailablePostsTypes, () => {
  fetchSpecificPostTypeData(reportId.value)
})

// Lifecycle Hooks
onMounted(async () => {
  platformName.value = 'instagram'
  setDateValues(props.reportDate)
  await getReportInfo()
  getDataTableMetrics(PLATFORMS.INSTAGRAM, reportId.value)
  getFollowersGrowthComparison(PLATFORMS.INSTAGRAM, reportId.value)
  getPostingActivityGraphByTypes(PLATFORMS.INSTAGRAM, reportId.value)
  getTopHashtags(PLATFORMS.INSTAGRAM, reportId.value)
  getBiographyData(PLATFORMS.INSTAGRAM, reportId.value)
  await fetchSpecificPostTypeData(reportId.value)
  getTopAndLeastPerformingPosts(PLATFORMS.INSTAGRAM, reportId.value)
})
</script>

<template>
  <div class="page_view flex flex-col justify-between">
    <div class="subpage">
      <h2 class="bg-gradient-to-r from-[#01AAFF] to-[#0ACADD] py-2 px-3 rounded-lg text-white my-2 text-base font-weight-500">
        Competitor Overview
      </h2>
      <div v-if="!dataTableMetricsResponse.length" class="grid grid-cols-5 gap-7 mb-7">
        <SkeletonBox class="!h-[8.5rem]" />
        <SkeletonBox class="!h-[8.5rem]" />
        <SkeletonBox class="!h-[8.5rem]" />
        <SkeletonBox class="!h-[8.5rem]" />
        <SkeletonBox class="!h-[8.5rem]" />
      </div>
      <transition-group
        v-else-if="dataTableMetricsResponse.length"
        tag="div"
        class="grid grid-cols-5 gap-7 mb-7"
        name="slide-fade"
      >
        <template v-for="(competitor, index) in dataTableMetricsResponse" :key="`competitor_tile_${index}`">
          <CompetitorTile
            :id="competitor.business_account_id"
            :name="competitor.name ? competitor.name : competitor.slug"
            :slug="competitor.slug"
            :image="competitor.image"
            :color="competitor.color"
            :followers="competitor.followersCount"
            :graph="getTileGraphValue(index)"
            :change="competitor.followersCountDiff"
            :state="competitor.state"
            :engagement-rate="competitor.engagementRate"
            :max-er="state.maxER"
            :min-er="state.minER"
            :max-followers="state.maxFollowers"
            :min-followers="state.minFollowers"
          />
          <CompetitorTile
            v-if="dataTableMetricsResponse.length < 5 && index + 1 === dataTableMetricsResponse.length"
            :key="`empty_tile${index}`"
            :is-empty="true"
            name=""
            image=""
            color=""
            followers=""
            @click="EventBus.$emit('show-manage-competitors-modal', reportCompetitorsInfo)"
          />
        </template>
      </transition-group>

      <div class="mb-7 relative">
        <h2 class="bg-gradient-to-r from-[#01AAFF] to-[#0ACADD] py-2 px-3 rounded-lg text-white my-2 text-base font-weight-500">
          Performance Summary
        </h2>
        <PerformanceComparison
          :title="performanceComparisonData.title"
          :legend="{ show: true, data: getPerformanceComparisonLegends }"
          :x-axis-label="performanceComparisonData.xAxisLabel"
          :y-axis-label="performanceComparisonData.yAxisLabel"
          :data="filteredDataTableMetrics"
          type="performanceComparison"
          :allow-data-zoom="false"
        />
      </div>
    </div>
    <ReportFooter :current-page="1" :total-pages="totalPages" />
  </div>

  <div class="page_view flex flex-col justify-between">
    <div class="subpage">
      <div class="mb-7">
        <CompetitorsTable
          :title="performanceReviewTableData.title"
          :data="filteredDataTableMetrics"
          :headers="performanceReviewTableData.headers"
          :followers-growth-data="getFollowersGrowthData"
          :type="performanceReviewTableData.type"
        />
      </div>
      <h2 class="bg-gradient-to-r from-[#01AAFF] to-[#0ACADD] py-2 px-3 rounded-lg text-white my-2 text-base font-weight-500">
        Follower Insights
      </h2>
      <div class="mb-7">
        <FollowersComparisonBarChart
          :title="performanceReviewGraphData.title"
          :x-axis-label="performanceReviewGraphData.xAxisLabel"
          :y-axis-label="performanceReviewGraphData.yAxisLabel"
          :data="filteredDataTableMetrics"
          :platform-name="PLATFORMS.INSTAGRAM"
          type="followersComparisonGraph"
        />
      </div>

      </div>
    <ReportFooter :current-page="2" :total-pages="totalPages" />
  </div>

  <div class="page_view flex flex-col justify-between">
    <div class="subpage">
      <div class="mb-7">
        <h2 class="bg-gradient-to-r from-[#01AAFF] to-[#0ACADD] py-2 px-3 rounded-lg text-white my-2 text-base font-weight-500">
        Post Type Insights
      </h2>
        <DoubleBarChart
          :title="postingByTypeData.title"
          :x-axis-label="postingByTypeData.xAxisLabel"
          :y-axis-label="postingByTypeData.yAxisLabel"
          :y-axis-second-label="postingByTypeData.yAxisSecondLabel"
          :data="postingActivityGraphByTypesResponse"
          :date-range-diff="dateRangeDiff"
          :colors="colors"
          :platform="platform"
          type="allTypesGraph"
        />
        </div>
        <div
          ref="specificPostingContent"
          class="mb-7"
      >
        <template v-if="specificPostingTypeResponses.length">
          <!-- First chart on the current page -->
          <DoubleBarChart
            v-if="specificPostingTypeResponses[0]"
            :title="`${getFormattedMediaType(specificPostingTypeResponses[0].type)} Post Performance`"
            :date-range-diff="dateRangeDiff"
            :data="specificPostingTypeResponses[0].activityData"
            :selected-type="getFormattedMediaType(specificPostingTypeResponses[0].type)"
            :all-available-types="getAllAvailablePostsTypes"
            :x-axis-label="postingBySpecificType.xAxisLabel"
            :y-axis-label="postingBySpecificType.yAxisLabel"
            :y-axis-second-label="postingBySpecificType.yAxisSecondLabel"
            :force-prop-title="true"
            type="postingActivityBySpecificType"
            :platform="platform"
            class="mb-8"
          />
        </template>
        </div>
        <ReportFooter :current-page="3" :total-pages="totalPages" />
      </div>
    </div>

    <!-- Remaining charts in pairs (two per page) -->
    <template v-for="(chunk, index) in paginatedPostTypeChunks" :key="index">
      <div class="page_view flex flex-col justify-between">
        <div class="subpage">
              <!-- First chart in this chunk -->
              <div v-if="chunk[0]" class="mb-8">
                <DoubleBarChart
                  :title="`${getFormattedMediaType(chunk[0].type)} Post Performance`"
                  :date-range-diff="dateRangeDiff"
                  :data="chunk[0].activityData"
                  :selected-type="getFormattedMediaType(chunk[0].type)"
                  :all-available-types="getAllAvailablePostsTypes"
                  :x-axis-label="postingBySpecificType.xAxisLabel"
                  :y-axis-label="postingBySpecificType.yAxisLabel"
                  :y-axis-second-label="postingBySpecificType.yAxisSecondLabel"
                  :force-prop-title="true"
                  type="postingActivityBySpecificType"
                  :platform="platform"
                />
              </div>

              <!-- Second chart in this chunk -->
              <div v-if="chunk[1]" class="mb-8">
                <DoubleBarChart
                  :title="`${getFormattedMediaType(chunk[1].type)} Post Performance`"
                  :date-range-diff="dateRangeDiff"
                  :data="chunk[1].activityData"
                  :selected-type="getFormattedMediaType(chunk[1].type)"
                  :all-available-types="getAllAvailablePostsTypes"
                  :x-axis-label="postingBySpecificType.xAxisLabel"
                  :y-axis-label="postingBySpecificType.yAxisLabel"
                  :y-axis-second-label="postingBySpecificType.yAxisSecondLabel"
                  :force-prop-title="true"
                  type="postingActivityBySpecificType"
                  :platform="platform"
                />
              </div>
            </div>
            <ReportFooter :current-page="4 + index" :total-pages="totalPages" />
          </div>
        </template>

  <div class="page_view">
    <div class="subpage">
      <div class="mb-7">
        <div class="mb-7">
        <h2 class="bg-gradient-to-r from-[#01AAFF] to-[#0ACADD] py-2 px-3 rounded-lg text-white my-2 text-base font-weight-500">
          Hashtag Performance Analysis
        </h2>
        <CompetitorsTable
          :title="hashtagsTableData.title"
          :show-info-cell="false"
          :data="topHashtagsResponse"
          :headers="hashtagsTableData.headers"
          :fetch-data-func="fetchSingleHashtagData"
          :type="hashtagsTableData.type"
          :single-hashtag-data="individualHashtagDataResponse"
          :single-hashtag-header="singleHashtagContent.headers"
          :index-to-sort="2"
          :platform-name="PLATFORMS.INSTAGRAM"
          @single-hashtag="fetchSingleHashtagData"
        />
          </div>
        </div>
      </div>
  </div>


  <div class="page_view flex flex-col justify-between">
    <div class="subpage">
      <div class="mb-7">
        <h2 class="bg-gradient-to-r from-[#01AAFF] to-[#0ACADD] py-2 px-3 rounded-lg text-white my-2 text-base font-weight-500">
          Page Bio Comparison
        </h2>
        <CompetitorsTable
          :title="bioAnalysisTableData.title"
          :data="getBioAnalysisTableData"
          :headers="bioAnalysisTableData.headers"
          :type="bioAnalysisTableData.type"
        />
      </div>
    </div>
    <ReportFooter :current-page="4" :total-pages="totalPages" />
  </div>
  <div class="page_view flex flex-col justify-between">
    <div class="subpage">
      <TopPerformingPostsReport :data="getTopAndLeastPostsData" :platform-name="PLATFORMS.INSTAGRAM" />
    </div>
    <ReportFooter :current-page="5" :total-pages="totalPages" />
  </div>
  <div class="page_view flex flex-col justify-between">
    <div class="subpage">
      <LeastPerformingPostsReport :data="getTopAndLeastPostsData" :platform-name="PLATFORMS.INSTAGRAM" />
    </div>
    <ReportFooter :current-page="6" :total-pages="totalPages" />
  </div>
</template>

<style scoped>
.page_view {
  page-break-after: always;
  min-height: 29.7cm;
}

.subpage {
  padding: 1cm;
  height: 100%;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(20px);
  opacity: 0;
}
</style>
